import { ElectricityPrice, PriceArea } from '../types';
import { format } from 'date-fns';
import { getVatMultiplier } from './subsidyCalculator';

// Average spot price per calendar month (øre/kWh, excl. VAT) from hourly prices Oct 2025 – Sep 2026.
// Oct–Dec are 2025 and Jan–Sep are 2026, so until Sep 2027 each entry is "the same month last year".
export const historicalPrices = {
  January: { NO1: 127.3, NO2: 123.1, NO3: 119.4, NO4: 83.2, NO5: 125.2 },
  February: { NO1: 125.2, NO2: 114.2, NO3: 119.7, NO4: 97.7, NO5: 123.5 },
  March: { NO1: 117.7, NO2: 116.0, NO3: 78.1, NO4: 18.7, NO5: 119.7 },
  April: { NO1: 111.1, NO2: 103.4, NO3: 103.3, NO4: 14.4, NO5: 122.7 },
  May: { NO1: 106.5, NO2: 108.4, NO3: 79.0, NO4: 39.0, NO5: 109.9 },
  June: { NO1: 82.8, NO2: 105.3, NO3: 59.9, NO4: 28.4, NO5: 68.7 },
  July: { NO1: 109.0, NO2: 115.2, NO3: 73.8, NO4: 17.2, NO5: 111.0 },
  August: { NO1: 132.3, NO2: 141.6, NO3: 100.7, NO4: 16.5, NO5: 124.8 },
  September: { NO1: 132.7, NO2: 141.1, NO3: 107.1, NO4: 36.0, NO5: 132.4 },
  October: { NO1: 58.7, NO2: 69.5, NO3: 28.6, NO4: 5.3, NO5: 56.0 },
  November: { NO1: 92.3, NO2: 94.0, NO3: 54.3, NO4: 35.7, NO5: 88.4 },
  December: { NO1: 81.5, NO2: 82.6, NO3: 58.7, NO4: 31.7, NO5: 78.2 }
};

export const getPriceAreaInfo = () => [
  { id: 'NO1', name: 'Oslo', description: 'Oslo / Øst-Norge' },
  { id: 'NO2', name: 'Kristiansand', description: 'Kristiansand / Sør-Norge' },
  { id: 'NO3', name: 'Trondheim', description: 'Trondheim / Midt-Norge' },
  { id: 'NO4', name: 'Tromsø', description: 'Tromsø / Nord-Norge' },
  { id: 'NO5', name: 'Bergen', description: 'Bergen / Vest-Norge' },
];

export const fetchElectricityPrices = async (
  date: Date,
  area: PriceArea
): Promise<ElectricityPrice[]> => {
  try {
    const year = format(date, 'yyyy');
    const monthDay = format(date, 'MM-dd');
    const url = `https://www.hvakosterstrommen.no/api/v1/prices/${year}/${monthDay}_${area}.json`;
    
    const response = await fetch(url, {
      mode: 'cors',
      headers: {
        'Accept': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch prices: ${response.statusText}`);
    }
    
    const data: ElectricityPrice[] = await response.json();
    
    // Add 25% VAT (NO4 / Nord-Norge is VAT exempt)
    const vatRate = getVatMultiplier(area);
    return data.map(price => ({
      ...price,
      NOK_per_kWh: price.NOK_per_kWh * vatRate,
      EUR_per_kWh: price.EUR_per_kWh * vatRate
    }));
    
  } catch (error) {
    console.error('Error fetching electricity prices:', error);
    // Return fallback data based on historical averages if API fails
    const currentMonth = format(date, 'MMMM') as keyof typeof historicalPrices;
    const fallbackPrice = historicalPrices[currentMonth]?.[area] || 100; // Default to 100 øre/kWh
    
    // Generate 24 hours of fallback data
    const vatRate = getVatMultiplier(area);
    return Array.from({ length: 24 }, (_, hour) => ({
      NOK_per_kWh: (fallbackPrice / 100) * vatRate, // Convert øre to NOK and add VAT
      EUR_per_kWh: (fallbackPrice / 100) * vatRate * 0.09, // Approximate EUR conversion
      EXR: 11.0, // Approximate exchange rate
      time_start: format(new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour), "yyyy-MM-dd'T'HH:mm:ss'Z'"),
      time_end: format(new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour + 1), "yyyy-MM-dd'T'HH:mm:ss'Z'")
    }));
  }
};